import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  IconButton,
  Avatar,
  HStack,
  Flex,
  Spinner,
  Image,
  Icon,
} from "@chakra-ui/react";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVolumeMute,
  FaVolumeUp,
  FaVideo,
  FaVideoSlash,
} from "react-icons/fa";
import { MdMicOff, MdFlipCameraAndroid } from "react-icons/md";
import { MdCall, MdCallEnd } from "react-icons/md";
import vvLogoIcon from "../../img/vartaverse-icon.png";
import { useAuth } from "../../context/AuthContext";
import { formatTimeSecondsToHHMMSS } from "../../logics/callLogic";

const VideoCallModal = ({
  isOpen,
  callDirection,
  remoteUserName,
  callData,
  callDuration,
  remoteUserVideoRef,
  remoteUserMuted,
  localUserVideoRef,
  isTalking,
  audioEnabled,
  videoEnabled,
  toggleAudio,
  toggleVideo,
  handleAnswerCall,
  handleEndCall,
  handleDeclineCall,
}) => {
  const { currentUser } = useAuth();

  return (
    <Modal isOpen={isOpen} size="full">
      <ModalOverlay />
      <ModalContent
        height="100vh"
        m={0}
        borderRadius="0"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader>
          <Flex width="100%" alignItems="center" justifyContent="space-between">
            <Box>
              <Image src={vvLogoIcon} alt="Logo" height="27px" width="auto" />
            </Box>
            <Box>
              <Text fontSize="lg">
                {callDirection === "INCOMING" && "Incoming Call"}
                {callDirection === "OUTGOING" && "Outgoing Call"}
                {callDirection === "ONGOING" && "Video Call"}
              </Text>
            </Box>
            <Box>
              {callDirection === "ONGOING" && (
                <Text fontSize="sm" color="gray.600">
                  {formatTimeSecondsToHHMMSS(callDuration)}
                </Text>
              )}
            </Box>
          </Flex>
        </ModalHeader>

        <ModalBody flex="1" position="relative" p={0}>
          {callDirection === "INCOMING" && (
            <Flex
              width="100%"
              height="100%"
              bg="gray.100"
              align="center"
              justify="center"
              direction="column"
            >
              <Avatar name={remoteUserName} size="xl" mb={4} />
              <Text fontSize="2xl">Incoming call from {remoteUserName}</Text>
            </Flex>
          )}
          {callDirection === "OUTGOING" && (
            <Flex
              width="100%"
              height="100%"
              bg="gray.100"
              align="center"
              justify="center"
              direction="column"
            >
              <Spinner size="xl" mb={4} />
              <Text fontSize="lg">{remoteUserName}</Text>
              <Text fontSize="2xl">
                {callData?.status === "RINGING" ? "Ringing..." : "Calling..."}
              </Text>
            </Flex>
          )}
          {callDirection === "ONGOING" && (
            <Box width="100%" height="100%" bg="black" position="relative">
              <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <video
                  autoPlay
                  playsInline
                  ref={remoteUserVideoRef}
                  style={{
                    width: "75vw",
                    height: "75vh",
                    objectFit: "cover",
                  }}
                />
              </Box>

              <Box
                position="absolute"
                bottom="20px"
                left="75px"
                transform="translateX(-50%)"
                bg="rgba(0, 0, 0, 0.6)"
                px={3}
                py={1}
                borderRadius="md"
              >
                <Flex alignItems="center">
                  <Text color="white" fontSize="xs" mr={1}>
                    {remoteUserName}
                  </Text>
                  {remoteUserMuted && (
                    <Icon as={MdMicOff} color="white" boxSize="3" />
                  )}
                </Flex>
              </Box>
            </Box>
          )}

          <Box
            width="200px"
            height="150px"
            bg="gray.700"
            borderRadius="md"
            overflow="hidden"
            position="absolute"
            bottom="20px"
            right="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="dark-lg"
          >
            <video autoPlay muted playsInline ref={localUserVideoRef} />
            <Box
              position="absolute"
              bottom="0"
              width="100%"
              bg="rgba(0, 0, 0, 0.7)"
              py={1}
              textAlign="center"
            >
              <Text color="white" fontSize="xs">
                {currentUser?.name}
              </Text>
            </Box>
          </Box>
        </ModalBody>

        <ModalFooter>
          {callDirection === "INCOMING" && (
            <HStack spacing={4} justify="center" w="100%">
              <Button
                leftIcon={<MdCallEnd />}
                aria-label="Decline Call"
                colorScheme="red"
                onClick={handleDeclineCall}
              >
                Decline
              </Button>
              <Button
                leftIcon={<MdCall />}
                aria-label="Accept Call"
                colorScheme="teal"
                onClick={handleAnswerCall}
              >
                Accept
              </Button>
            </HStack>
          )}
          {callDirection === "OUTGOING" && (
            <HStack spacing={4} justify="center" w="100%">
              <Button colorScheme="red" onClick={handleEndCall}>
                Cancel
              </Button>
            </HStack>
          )}
          {callDirection === "ONGOING" && (
            <HStack w="100%" justify="center" spacing={4}>
              <IconButton
                aria-label="Toggle Audio"
                icon={audioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
                onClick={toggleAudio}
                variant="outline"
                color={audioEnabled ? "green.500" : "red.500"}
              />
              {/* <IconButton
                aria-label="Toggle Speaker"
                icon={isSpeakerOn ? <FaVolumeUp /> : <FaVolumeMute />}
                onClick={toggleSpeaker}
                variant="outline"
                colorScheme={isSpeakerOn ? "green.500" : "red.500"}
              /> */}
              <IconButton
                aria-label="Toggle Video"
                icon={videoEnabled ? <FaVideo /> : <FaVideoSlash />}
                color={videoEnabled ? "teal.500" : "red.500"}
                onClick={toggleVideo}
              />
              {/* {videoCameras.length > 1 && (
                <IconButton
                  aria-label="Toggle Camera"
                  icon={<MdFlipCameraAndroid />}
                  onClick={toggleCamera}
                />
              )} */}
              <IconButton
                aria-label="End Video"
                icon={<MdCallEnd />}
                colorScheme="red"
                onClick={handleEndCall}
                p={2}
              />
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VideoCallModal;
